import { useCallback, useEffect } from 'react';

import { DispatchedEvents } from '../types/dispatchedEvents';
import { EventTriggers } from '../types/eventTriggers';
import { useWindowMessaging } from './useWindowMessaging';

declare global {
    interface Window {
        JSBridge: any;
        webkit: any;
        chrome: any;
        sendMessageToApplication(obj): any;
    }
}

export const useAppMessaging = () => {
    const dispatch = (customEvent, payload) => window.dispatchEvent(new CustomEvent(customEvent, { detail: payload }));
    /**
     * This is the only thing to update when
     * new messages and events are implemented.
     */
    const forwardMessage = useCallback((message) => {
        switch (message.data.func) {
            case EventTriggers.BreakoutRoomActiveUsersUpdated: dispatch(DispatchedEvents.ReceivedParticipantBreakoutState, message.data); break;
            case EventTriggers.BreakoutRoomCreatorStateUpdated: dispatch(DispatchedEvents.ReceivedCreatorBreakoutState, message.data); break;
            case EventTriggers.BreakoutRoomStartedStatusUpdated: dispatch(DispatchedEvents.ReceivedBreakoutRoomStartedStatus, message.data); break;
            default: break;
        }
    }, []);

    const { sendToParent } = useWindowMessaging(forwardMessage);

    useEffect(() => {
        if (window.JSBridge) {
            window.JSBridge.addEventListener('message', forwardMessage);
        } else if (window.webkit && window.webkit.addEventListener) {
            window.webkit.addEventListener('message', forwardMessage);
        } else if (window.chrome && window.chrome.webview && window.chrome.webview.addEventListener) {
            window.chrome.webview.addEventListener('message', forwardMessage);
        } else {
            window.addEventListener('message', forwardMessage);
        }

        return () => {
            if (window.JSBridge) {
                window.JSBridge.removeEventListener('message', forwardMessage);
            } else if (window.webkit?.removeEventListener) {
                window.webkit.removeEventListener('message', forwardMessage);
            } else if (window.chrome?.webview?.removeEventListener) {
                window.chrome.webview.removeEventListener('message', forwardMessage);
            } else {
                window.removeEventListener('message', forwardMessage);
            }
        };
    }, [forwardMessage]);

    const sendMessage = useCallback((msg) => {
        if (window.JSBridge) {
            window.JSBridge.sendMessageToApplication(JSON.stringify(msg));
        } else if (window.webkit?.messageHandlers?.jsMessageHandler) {
            window.webkit.messageHandlers.jsMessageHandler.postMessage(JSON.stringify(msg));
        } else if (window.chrome?.webview?.postMessage) {
            window.chrome.webview.postMessage(msg);
        } else if ((window.opener && window.opener !== window) || (window.parent !== window)) {
            window.parent.postMessage(JSON.stringify(msg), '*');
        } else if (typeof window.sendMessageToApplication === 'function') {
            window.sendMessageToApplication(JSON.stringify(msg));
        } else {
            sendToParent(msg);
        }
    }, [sendToParent]);

    const closeWindow = useCallback(() => {
        sendMessage({ func: 'close' });
    }, [sendMessage]);

    return { sendMessage, closeWindow };
};
